import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { TokenCacheService } from './token-cache.service';
import {
  AuthContext,
  detectAuthContext,
  extractRestaurantSlug,
  getLoginRouteForContext
} from '../utils/auth-context.util';
import { isJwtExpired } from '../utils/jwt.util';

const TOAST_KEY = 'session';
const TOAST_LIFE_MS = 6000;
/**
 * Ventana de silencio tras manejar una expiracion. Mientras este activa, todo
 * 401 adicional se ignora — no re-muestra toast ni re-navega. Es la defensa
 * robusta contra el bucle: las fuentes recurrentes de 401 que sobreviven al
 * redirect (polling residual, reconexiones SignalR con token muerto, requests
 * en vuelo que resuelven tarde) ya no pueden re-disparar el mensaje "a cada
 * rato". 10s cubre de sobra la rafaga de reconexion sin bloquear una expiracion
 * futura genuina.
 */
const HANDLE_THROTTLE_MS = 10000;

/**
 * Centraliza la respuesta a una sesion expirada (token JWT caducado o
 * revocado). Lo invoca el interceptor HTTP cuando recibe 401 y el
 * realtime-hub cuando reconecta y la negociacion devuelve 401.
 *
 * Garantiza un solo redirect + toast aunque se llame varias veces en rapida
 * sucesion (caso tipico: app vuelve de background y dispara N requests
 * concurrentes que reciben 401 juntos).
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiredHandlerService {
  private tokenCache = inject(TokenCacheService);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);

  private dispatched = false;
  private lastHandledAt = 0;

  /**
   * Limpia el token del contexto, muestra toast y redirige al login
   * correspondiente. Es idempotente dentro de una "sesion expirada" —
   * el flag se resetea automaticamente cuando el usuario navega al login,
   * y un throttle por tiempo bloquea cualquier 401 recurrente posterior.
   */
  handle(currentUrl?: string): void {
    const now = Date.now();
    if (now - this.lastHandledAt < HANDLE_THROTTLE_MS) return;
    if (this.dispatched) return;

    const url = currentUrl ?? this.router.url;
    if (this.isOnLoginPage(url)) return;

    const context = detectAuthContext(url);

    // PROTECCION CLAVE: solo expulsar si el token de ESTE contexto esta realmente VENCIDO o
    // ausente. Un 401 con token aun vigente es ESPURIO (reconexion SignalR durante un deploy,
    // request puntual no autorizada, hipo de red) -> NO se toca la sesion. Sin este chequeo,
    // un solo 401 cualquiera sacaba al usuario aunque su token durara dias ("se sale en voleo").
    const token = this.getTokenForContext(context);
    if (token && !isJwtExpired(token)) {
      return;
    }

    this.dispatched = true;
    this.lastHandledAt = now;
    this.clearTokenForContext(context);
    this.showExpiredToast();
    this.redirectToLogin(context, url);
  }

  /**
   * Hook que el guard de cada login llama al inicializarse para que el
   * proximo 401 vuelva a disparar el flujo. Sin esto, despues del primer
   * redirect el handler quedaria "trabado" en dispatched=true y no
   * mostraria nada en sesiones futuras.
   */
  reset(): void {
    this.dispatched = false;
  }

  /** Token del contexto activo (mismo mapa que clearTokenForContext) para chequear su exp. */
  private getTokenForContext(context: AuthContext): string | null {
    const getters: Record<AuthContext, () => string | null> = {
      owner: () => this.tokenCache.getAuthToken(),
      superadmin: () => this.tokenCache.getAuthToken(),
      pos: () => this.tokenCache.getPosToken(),
      'pos-simple': () => this.tokenCache.getPosToken(),
      kitchen: () => this.tokenCache.getKitchenToken(),
      bar: () => this.tokenCache.getBarToken(),
      waiter: () => this.tokenCache.getWaiterToken(),
      delivery: () => this.tokenCache.getDeliveryToken(),
      kiosk: () => this.getKioskToken()
    };
    return getters[context]();
  }

  private getKioskToken(): string | null {
    try {
      const raw = localStorage.getItem('pidepaya_kiosk_session');
      return raw ? (JSON.parse(raw)?.token ?? null) : null;
    } catch {
      return null;
    }
  }

  private clearTokenForContext(context: AuthContext): void {
    const cleaners: Record<AuthContext, () => void> = {
      owner: () => this.tokenCache.clearAuthToken(),
      superadmin: () => this.tokenCache.clearAuthToken(),
      pos: () => this.tokenCache.clearPosToken(),
      'pos-simple': () => this.tokenCache.clearPosToken(),
      kitchen: () => this.tokenCache.clearKitchenToken(),
      bar: () => this.tokenCache.clearBarToken(),
      waiter: () => this.tokenCache.clearWaiterToken(),
      delivery: () => this.tokenCache.clearDeliveryToken(),
      kiosk: () => this.clearKioskSession()
    };
    cleaners[context]();
  }

  private clearKioskSession(): void {
    try {
      localStorage.removeItem('pidepaya_kiosk_session');
    } catch {
      // storage puede fallar en modo privado/iframe
    }
  }

  private showExpiredToast(): void {
    this.messageService.add({
      key: TOAST_KEY,
      severity: 'warn',
      summary: this.translate.instant('auth.session.expiredTitle'),
      detail: this.translate.instant('auth.session.expiredDetail'),
      life: TOAST_LIFE_MS
    });
  }

  private redirectToLogin(context: AuthContext, url: string): void {
    const slug = extractRestaurantSlug(url, context);
    const target = getLoginRouteForContext(context, slug);
    if (!target) {
      this.dispatched = false;
      return;
    }
    this.router.navigateByUrl(target).finally(() => {
      this.dispatched = false;
    });
  }

  private isOnLoginPage(url: string): boolean {
    const path = url.split('?')[0];
    const loginSegments = ['/login', '/mesero', '/cocina', '/barra', '/pos', '/pos-simple', '/kiosco', '/delivery'];
    return loginSegments.some(segment => path.endsWith(segment));
  }
}
